import React, { useState, useEffect } from 'react';
import { SiteToken, SyncStats } from '../types';
import api, { apiService } from '../services/api';
import { validateConfig } from '../utils/configSchema';
import toast from 'react-hot-toast';
import { triggerBuild, ActiveRun } from '../hooks/useBuildManager';
import BuildDownloadsModal from '../components/BuildDownloadsModal';
import BuildModal from '../components/BuildModal';
import BuildStatusCell from '../components/BuildStatusCell';
import DataTable, { Column } from '../components/DataTable';

const SiteTokens: React.FC = () => {
  const [siteTokens, setSiteTokens] = useState<SiteToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncStats | null>(null);
  const [selectedToken, setSelectedToken] = useState<SiteToken | null>(null);

  // Config version management states
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [editingToken, setEditingToken] = useState<SiteToken | null>(null);
  const [configVersions, setConfigVersions] = useState<any[]>([]);
  const [configJson, setConfigJson] = useState('');
  const [impressaConfigJson, setImpressaConfigJson] = useState('');
  const [uiConfigJson, setUiConfigJson] = useState('');
  const [versionLoading, setVersionLoading] = useState(false);
  const [editingVersionId, setEditingVersionId] = useState<number | null>(null);

  // Detail view states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [tokenDetail, setTokenDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [copiedTokenId, setCopiedTokenId]         = useState<number | null>(null);

  const copyToken = async (id: number, token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedTokenId(id);
      setTimeout(() => setCopiedTokenId(null), 3000);
    } catch {
      // clipboard unavailable (HTTP context) — show token in prompt as fallback
      window.prompt('Copy token:', token);
    }
  };

  const [showBuildModal, setShowBuildModal]       = useState(false);
  const [buildingToken, setBuildingToken]         = useState<SiteToken | null>(null);
  const [selectedTag, setSelectedTag]             = useState('');
  const [activeRun, setActiveRun]                 = useState<ActiveRun | undefined>(undefined);
  const [triggerError, setTriggerError]           = useState<string | null>(null);
  const [isTriggering, setIsTriggering]           = useState(false);


  const [showDownloadsModal, setShowDownloadsModal] = useState(false);
  const [downloadsToken, setDownloadsToken]         = useState<SiteToken | null>(null);
  const [downloadsVersions, setDownloadsVersions]       = useState<{ version: string; electron_tag: string; filename: string }[]>([]);
  const [downloadsLoading, setDownloadsLoading]         = useState(false);

  const openDownloadsModal = async (token: SiteToken) => {
    setDownloadsToken(token);
    setDownloadsVersions([]);
    setShowDownloadsModal(true);
    setDownloadsLoading(true);
    try {
      const { versions } = await apiService.listArtifacts(token.workspace_name);
      setDownloadsVersions(versions);
    } catch {
      // ignore — empty list shown
    } finally {
      setDownloadsLoading(false);
    }
  };

  useEffect(() => {
    loadSiteTokens();
  }, []);

  const loadSiteTokens = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getSiteTokens();
      setSiteTokens(data);
    } catch (err) {
      setError('Failed to load site tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSyncResult(null);

      const response = await apiService.syncFromPlatform();
      setSyncResult(response.stats);

      await loadSiteTokens();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Sync failed. Please check the platform API connection.');
    } finally {
      setSyncing(false);
    }
  };

  const refreshVersions = async (token: SiteToken) => {
    try {
      const response = await apiService.getSiteTokenConfigHistory(token.id);
      const sorted = response.history.sort((a: any, b: any) => {
        if (a.is_active && !b.is_active) return -1;
        if (!a.is_active && b.is_active) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setConfigVersions(sorted);
    } catch (err) {
      setError('Failed to load config versions');
    }
  };

  const openVersionModal = async (token: SiteToken) => {
    setEditingToken(token);
    setVersionLoading(true);
    setShowVersionModal(true);
    try {
      const response = await apiService.getSiteTokenConfigHistory(token.id);
      const sorted = response.history.sort((a: any, b: any) => {
        if (a.is_active && !b.is_active) return -1;
        if (!a.is_active && b.is_active) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setConfigVersions(sorted);
    } catch (err) {
      setError('Failed to load config versions');
    } finally {
      setVersionLoading(false);
    }
  };


  const openBuildModal = async (token: SiteToken) => {
    setBuildingToken(token);
    setSelectedTag('electron-');
    setActiveRun(undefined);
    setTriggerError(null);
    setShowBuildModal(true);
    try {
      const workflowRes = await apiService.getWorkflowStatus();
      // If a run is already active, skip to in-progress view immediately
      if (workflowRes.active && workflowRes.run) {
        setActiveRun(workflowRes.run as any);
      }
    } catch {
      setTriggerError('Could not load build status. Check your connection.');
    }
  };

  const startBuild = async () => {
    if (!buildingToken || !selectedTag) return;
    setIsTriggering(true);
    try {
      const { alreadyRunning, activeRun: run } = await triggerBuild(buildingToken.id, selectedTag);
      if (alreadyRunning && run) {
        setActiveRun(run);
      } else {
        setShowBuildModal(false);
      }
    } catch (err: any) {
      setTriggerError(err.response?.data?.error || 'Failed to trigger build');
    } finally {
      setIsTriggering(false);
    }
  };

  const openAddVersionModal = (existingConfig?: any, existingImpressaConfig?: any, existingUiConfig?: any, versionNumber?: number) => {
    setConfigJson(JSON.stringify(existingConfig || {}, null, 2));
    setImpressaConfigJson(JSON.stringify(existingImpressaConfig || {}, null, 2));
    setUiConfigJson(JSON.stringify(existingUiConfig || {}, null, 2));
    setEditingVersionId(versionNumber || null);
    setShowAddVersionModal(true);
  };

  const openDetailModal = async (token: SiteToken) => {
    setShowDetailModal(true);
    setDetailLoading(true);
    try {
      const detail = await apiService.getSiteTokenDetail(token.id);
      setTokenDetail(detail);
    } catch (err: any) {
      if (err.response?.data) {
        setTokenDetail(err.response.data);
      } else {
        setError('Failed to load site token details');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAddVersion = async (setActive: boolean) => {
    if (!editingToken) return;

    let config: any, impressaConfig: any, uiConfig: any;
    try {
      config = JSON.parse(configJson);
    } catch (err: any) {
      toast.error(`Invalid JSON in Config: ${err.message}`);
      return;
    }
    try {
      impressaConfig = JSON.parse(impressaConfigJson);
    } catch (err: any) {
      toast.error(`Invalid JSON in Impressa Config: ${err.message}`);
      return;
    }
    try {
      uiConfig = JSON.parse(uiConfigJson);
    } catch (err: any) {
      toast.error(`Invalid JSON in UI Config: ${err.message}`);
      return;
    }

    if (Object.keys(config).length > 0) {
      const validationError = validateConfig(config);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    try {
      if (editingVersionId) {
        await apiService.updateSiteTokenConfig(editingToken.id, editingVersionId, config, setActive);
        await apiService.updateSiteTokenImpressaConfig(editingToken.id, editingVersionId, impressaConfig, setActive);
        await apiService.updateSiteTokenUIConfig(editingToken.id, editingVersionId, uiConfig, setActive);
      } else {
        await apiService.createSiteTokenConfigVersion(editingToken.id, config, impressaConfig, uiConfig, setActive);
      }

      setShowAddVersionModal(false);
      setEditingVersionId(null);
      setError(null);
      await refreshVersions(editingToken);
      await loadSiteTokens();
    } catch (err: any) {
      toast.error(err.response?.data?.error || (editingVersionId ? 'Failed to update version' : 'Failed to create version'));
    }
  };

  const handleActivateVersion = async (versionId: number) => {
    if (!editingToken) return;
    try {
      const version = configVersions.find(v => v.id === versionId);
      if (!version) return;
      await api.patch(`/auth/tokens/${editingToken.id}/config/activate/`, {
        version: version.version
      });
      await refreshVersions(editingToken);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to activate version');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const columns: Column<SiteToken>[] = [
    {
      key: 'workspace',
      header: 'Workspace',
      skeletonWidth: 'w-40',
      render: (token) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <span className="text-sm font-bold text-gray-900 truncate max-w-[200px]" title={token.workspace_name || 'N/A'}>
            {token.workspace_name || 'N/A'}
          </span>
        </div>
      ),
    },
    {
      key: 'token',
      header: 'Token',
      skeletonWidth: 'w-32',
      render: (token) => (
        <code
          onClick={() => copyToken(token.id, token.token)}
          title="Click to copy"
          className={`text-[11px] font-mono px-2 py-1 rounded-md cursor-pointer select-none transition-colors duration-300 ${
            copiedTokenId === token.id
              ? 'text-green-700 bg-green-100'
              : 'text-gray-900 bg-slate-200 hover:bg-slate-300'
          }`}
        >
          {copiedTokenId === token.id ? '✓ Copied!' : token.token}
        </code>
      ),
    },
    {
      key: 'users',
      header: 'Users',
      align: 'center',
      skeletonWidth: 'w-8',
      render: (token) => (
        <span className="text-sm font-semibold text-gray-900">{token.site_users_count}</span>
      ),
    },
    {
      key: 'machines',
      header: 'Machines',
      align: 'center',
      skeletonWidth: 'w-8',
      render: (token) => (
        <span className="text-sm font-semibold text-gray-900">{token.site_machines_count}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      skeletonWidth: 'w-28',
      render: (token) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => openDetailModal(token)}
            title="View details"
            className="p-2 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => openVersionModal(token)}
            title="Configure"
            className="p-2 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          <button
            onClick={() => openDownloadsModal(token)}
            title="Download builds"
            className="p-2 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
          <div className="h-4 w-px bg-slate-200 mx-1" />
          <BuildStatusCell onOpenModal={() => openBuildModal(token)} />
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-8 shadow-sm">
      <div className="flex justify-between items-start mb-8 pb-5 border-b-2 border-gray-200">
        <div>
          <h2 className="text-3xl font-semibold text-gray-800 mb-1">Site Tokens</h2>
          <p className="text-gray-600 text-base">Manage site tokens synced from the platform API</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed"
        >
          {syncing ? 'Syncing...' : 'Sync from Platform'}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-300 text-red-900 px-5 py-4 rounded mb-5 flex justify-between items-center">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="bg-transparent border-0 text-red-900 cursor-pointer font-medium underline">Dismiss</button>
        </div>
      )}

      {triggerError && (
        <div className="bg-red-100 border border-red-300 text-red-900 px-5 py-4 rounded mb-5 flex justify-between items-center">
          <p>{triggerError}</p>
          <button onClick={() => setTriggerError(null)} className="bg-transparent border-0 text-red-900 cursor-pointer font-medium underline">Dismiss</button>
        </div>
      )}

      {syncResult && (
        <div className="bg-cyan-50 border border-cyan-300 text-cyan-900 p-5 rounded-lg mb-5">
          <h3 className="text-lg font-semibold mb-4">Sync Results</h3>
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex gap-2">
              <span className="font-medium">Sites Created:</span>
              <span className="font-semibold">{syncResult.sites_created}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-medium">Sites Updated:</span>
              <span className="font-semibold">{syncResult.sites_updated}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-medium">Users Created:</span>
              <span className="font-semibold">{syncResult.users_created}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-medium">Users Updated:</span>
              <span className="font-semibold">{syncResult.users_updated}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-medium">Machines Created:</span>
              <span className="font-semibold">{syncResult.machines_created}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-medium">Machines Updated:</span>
              <span className="font-semibold">{syncResult.machines_updated}</span>
            </div>
          </div>
          {syncResult.errors.length > 0 && (
            <div className="bg-red-100 text-red-900 p-3 rounded mb-4">
              <h4 className="font-semibold mb-2">Errors:</h4>
              <ul className="list-disc pl-5">
                {syncResult.errors.map((err, idx) => (
                  <li key={idx}>Site {err.site_id}: {err.error}</li>
                ))}
              </ul>
            </div>
          )}
          <button onClick={() => setSyncResult(null)} className="bg-cyan-900 hover:bg-cyan-950 text-white py-2 px-4 rounded text-sm transition-colors">
            Dismiss
          </button>
        </div>
      )}

      {/* Stats row */}
      <div className="flex gap-4 mb-6 flex-wrap">
        {[
          { label: 'Sites', value: siteTokens.length, color: 'blue' },
          { label: 'Users', value: siteTokens.reduce((a, t) => a + t.site_users_count, 0), color: 'violet' },
          { label: 'Machines', value: siteTokens.reduce((a, t) => a + t.site_machines_count, 0), color: 'emerald' },
        ].map(({ label, value, color }) => (
          <div key={label} className={`flex-1 min-w-[140px] bg-white border border-slate-100 rounded-xl p-5 shadow-sm`}>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
            <p className={`text-3xl font-bold text-${color}-600 m-0 leading-tight`}>{value}</p>
          </div>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={siteTokens}
        rowKey={(t) => t.id}
        loading={loading}
        skeletonRows={6}
        rowClassName={(t) => (!t.is_active ? 'opacity-50' : '')}
        emptyMessage={
          <>
            No site tokens found. Click{' '}
            <span className="font-semibold text-slate-500">Sync from Platform</span> to fetch tokens.
          </>
        }
      />

      {selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1000]" onClick={() => setSelectedToken(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-[90%] max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center py-5 px-5 border-b border-gray-200">
              <h3 className="text-xl text-gray-800 font-semibold m-0">Token Details: {selectedToken.workspace_name}</h3>
              <button onClick={() => setSelectedToken(null)} className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 hover:text-gray-800 p-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors">&times;</button>
            </div>
            <div className="p-5">
              <div className="mb-6">
                <h4 className="text-base font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 m-0">General Information</h4>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="font-semibold text-gray-600 text-xs">Platform ID:</label>
                    <span className="text-gray-800">{selectedToken.platform_id}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="font-semibold text-gray-600 text-xs">Token:</label>
                    <code className="text-gray-800 font-mono bg-gray-50 py-0.5 px-1.5 rounded text-sm break-all">{selectedToken.token}</code>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="font-semibold text-gray-600 text-xs">Workspace ID:</label>
                    <span className="text-gray-800">{selectedToken.workspace_id || 'N/A'}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="font-semibold text-gray-600 text-xs">Status:</label>
                    <span className={`inline-block py-1 px-2.5 rounded text-xs font-medium ${selectedToken.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-900'}`}>
                      {selectedToken.is_active ? 'Active' : 'Revoked'}
                    </span>
                  </div>
                </div>
              </div>

              {selectedToken.site_users && selectedToken.site_users.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-base font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 m-0">Users ({selectedToken.site_users.length})</h4>
                  <table className="w-full border-collapse mt-2">
                    <thead>
                      <tr>
                        <th className="py-2 px-3 text-left border-b border-gray-200 text-sm bg-gray-50 font-semibold text-gray-800">Username</th>
                        <th className="py-2 px-3 text-left border-b border-gray-200 text-sm bg-gray-50 font-semibold text-gray-800">Email</th>
                        <th className="py-2 px-3 text-left border-b border-gray-200 text-sm bg-gray-50 font-semibold text-gray-800">Status</th>
                        <th className="py-2 px-3 text-left border-b border-gray-200 text-sm bg-gray-50 font-semibold text-gray-800">UUID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedToken.site_users.map(user => (
                        <tr key={user.id}>
                          <td className="py-2 px-3 text-left border-b border-gray-200 text-sm text-gray-600">{user.username}</td>
                          <td className="py-2 px-3 text-left border-b border-gray-200 text-sm text-gray-600">{user.email}</td>
                          <td className="py-2 px-3 text-left border-b border-gray-200 text-sm text-gray-600">
                            <span className={`inline-block py-1 px-2.5 rounded text-xs font-medium ${user.approved.toLowerCase() === 'approved' ? 'bg-green-100 text-green-800' : user.approved.toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-900'}`}>
                              {user.approved}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-left border-b border-gray-200 text-sm text-gray-600"><code className="font-mono text-sm">{user.uuid.substring(0, 8)}...</code></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedToken.site_machines && selectedToken.site_machines.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-base font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 m-0">Machines ({selectedToken.site_machines.length})</h4>
                  <table className="w-full border-collapse mt-2">
                    <thead>
                      <tr>
                        <th className="py-2 px-3 text-left border-b border-gray-200 text-sm bg-gray-50 font-semibold text-gray-800">Hostname</th>
                        <th className="py-2 px-3 text-left border-b border-gray-200 text-sm bg-gray-50 font-semibold text-gray-800">Token</th>
                        <th className="py-2 px-3 text-left border-b border-gray-200 text-sm bg-gray-50 font-semibold text-gray-800">Status</th>
                        <th className="py-2 px-3 text-left border-b border-gray-200 text-sm bg-gray-50 font-semibold text-gray-800">OS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedToken.site_machines.map(machine => (
                        <tr key={machine.id}>
                          <td className="py-2 px-3 text-left border-b border-gray-200 text-sm text-gray-600">{machine.system_details?.hostname || 'Unknown'}</td>
                          <td className="py-2 px-3 text-left border-b border-gray-200 text-sm text-gray-600"><code className="font-mono text-sm">{machine.token.substring(0, 12)}...</code></td>
                          <td className="py-2 px-3 text-left border-b border-gray-200 text-sm text-gray-600">
                            <span className={`inline-block py-1 px-2.5 rounded text-xs font-medium ${machine.approved.toLowerCase() === 'approved' ? 'bg-green-100 text-green-800' : machine.approved.toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-900'}`}>
                              {machine.approved}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-left border-b border-gray-200 text-sm text-gray-600">{machine.system_details?.os || 'Unknown'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedToken.config && Object.keys(selectedToken.config).length > 0 && (
                <div className="mb-6">
                  <h4 className="text-base font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 m-0">Configuration</h4>
                  <pre className="bg-gray-50 border border-gray-200 rounded p-4 font-mono text-xs max-h-[200px] overflow-y-auto whitespace-pre-wrap break-words">
                    {JSON.stringify(selectedToken.config, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Config Version Management Modal */}
      {showVersionModal && editingToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1000]" onClick={() => setShowVersionModal(false)}>
          <div className="bg-white rounded-lg max-w-4xl w-[95%] max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center py-5 px-5 border-b border-gray-200">
              <h3 className="text-xl text-gray-800 font-semibold m-0">Config Versions - {editingToken.workspace_name}</h3>
              <button onClick={() => setShowVersionModal(false)} className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 hover:text-gray-800 p-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors">&times;</button>
            </div>
            <div className="p-5">
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => openAddVersionModal()}
                  className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors"
                >
                  + Add New Version
                </button>
              </div>

              {versionLoading ? (
                <p className="text-gray-500 text-center py-5">Loading versions...</p>
              ) : configVersions.length === 0 ? (
                <p className="text-gray-500 text-center py-5">No config versions found.</p>
              ) : (
                <div className="flex flex-col gap-4">
                  {configVersions.map((version) => (
                    <div key={version.id} className={`bg-white border-2 rounded-lg p-4 ${version.is_active ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <h4 className="text-lg font-semibold text-gray-800 m-0">Version {version.version}</h4>
                          {version.is_active && (
                            <span className="bg-green-600 text-white text-xs px-2 py-1 rounded font-semibold">ACTIVE</span>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">{formatDate(version.created_at)}</div>
                          <div className="text-xs text-gray-500">by {version.created_by || 'System'}</div>
                        </div>
                      </div>
                      <div className="mb-3 flex flex-col gap-3">
                        <div>
                          <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Config</div>
                          <pre className="bg-gray-50 border border-gray-200 rounded p-3 m-0 text-xs max-h-[150px] overflow-y-auto font-mono">{JSON.stringify(version.config, null, 2)}</pre>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Impressa Config</div>
                          <pre className="bg-gray-50 border border-gray-200 rounded p-3 m-0 text-xs max-h-[150px] overflow-y-auto font-mono">{JSON.stringify(version.impressa_config, null, 2)}</pre>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-gray-500 uppercase mb-1">UI Config</div>
                          <pre className="bg-gray-50 border border-gray-200 rounded p-3 m-0 text-xs max-h-[150px] overflow-y-auto font-mono">{JSON.stringify(version.ui_config, null, 2)}</pre>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openAddVersionModal(version.config, version.impressa_config, version.ui_config, version.is_active ? undefined : version.version)}
                          className="bg-gray-600 hover:bg-gray-700 text-white py-1.5 px-4 rounded text-sm transition-colors"
                        >
                          Edit
                        </button>
                        {!version.is_active && (
                          <button
                            onClick={() => handleActivateVersion(version.id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white py-1.5 px-4 rounded text-sm transition-colors"
                          >
                            Set as Active
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add New Version Modal */}
      {showAddVersionModal && editingToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1001]" onClick={() => { setShowAddVersionModal(false); setEditingVersionId(null); }}>
          <div className="bg-white rounded-lg max-w-3xl w-[95%] max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center py-5 px-5 border-b border-gray-200">
              <h3 className="text-xl text-gray-800 font-semibold m-0">
                {editingVersionId ? 'Edit Config Version' : 'Add New Config Version'}
              </h3>
              <button onClick={() => { setShowAddVersionModal(false); setEditingVersionId(null); }} className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 hover:text-gray-800 p-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors">&times;</button>
            </div>
            <div className="p-5 flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-gray-800 text-sm">Config (JSON)</label>
                <textarea
                  className="w-full font-mono text-sm p-3 border border-gray-300 rounded resize-y bg-gray-50 leading-6 focus:outline-none focus:border-blue-600 focus:bg-white transition-all"
                  value={configJson}
                  onChange={(e) => setConfigJson(e.target.value)}
                  rows={8}
                  placeholder='{"key": "value"}'
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-gray-800 text-sm">Impressa Config (JSON)</label>
                <textarea
                  className="w-full font-mono text-sm p-3 border border-gray-300 rounded resize-y bg-gray-50 leading-6 focus:outline-none focus:border-blue-600 focus:bg-white transition-all"
                  value={impressaConfigJson}
                  onChange={(e) => setImpressaConfigJson(e.target.value)}
                  rows={8}
                  placeholder='{"key": "value"}'
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-gray-800 text-sm">UI Config (JSON)</label>
                <textarea
                  className="w-full font-mono text-sm p-3 border border-gray-300 rounded resize-y bg-gray-50 leading-6 focus:outline-none focus:border-blue-600 focus:bg-white transition-all"
                  value={uiConfigJson}
                  onChange={(e) => setUiConfigJson(e.target.value)}
                  rows={8}
                  placeholder='{"key": "value"}'
                />
              </div>
              <div className="flex gap-3 justify-end pt-5 border-t border-gray-200">
                <button onClick={() => { setShowAddVersionModal(false); setEditingVersionId(null); }} className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-5 rounded cursor-pointer text-sm transition-colors">Cancel</button>
                {editingVersionId ? (
                  <>
                    <button onClick={() => handleAddVersion(false)} className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-5 rounded cursor-pointer text-sm transition-colors">Save</button>
                    <button onClick={() => handleAddVersion(true)} className="bg-green-600 hover:bg-green-700 text-white py-2 px-5 rounded cursor-pointer text-sm transition-colors">Save & Set Active</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleAddVersion(false)} className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-5 rounded cursor-pointer text-sm transition-colors">Save as Draft</button>
                    <button onClick={() => handleAddVersion(true)} className="bg-green-600 hover:bg-green-700 text-white py-2 px-5 rounded cursor-pointer text-sm transition-colors">Save & Set Active</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showDownloadsModal && downloadsToken && (
        <BuildDownloadsModal
          siteName={downloadsToken.workspace_name}
          versions={downloadsVersions}
          isLoading={downloadsLoading}
          onClose={() => setShowDownloadsModal(false)}
        />
      )}

      {showBuildModal && buildingToken && (
        <BuildModal
          siteName={buildingToken.workspace_name}
          isTriggering={isTriggering}
          selectedTag={selectedTag}
          triggerError={triggerError}
          activeRun={activeRun}
          onTagChange={setSelectedTag}
          onStart={startBuild}
          onClose={() => setShowBuildModal(false)}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1002]" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-lg max-w-4xl w-[95%] max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center py-5 px-5 border-b border-gray-200">
              <h3 className="text-xl text-gray-800 font-semibold m-0">Site Token Details</h3>
              <button onClick={() => setShowDetailModal(false)} className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 hover:text-gray-800 p-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors">&times;</button>
            </div>
            <div className="p-5">
              {detailLoading ? (
                <p className="text-gray-500 text-center py-5">Loading details...</p>
              ) : tokenDetail?.error ? (
                <div className="bg-red-100 border border-red-300 text-red-900 px-5 py-4 rounded">
                  <p className="font-semibold mb-2">Error loading details:</p>
                  <p>{tokenDetail.error}</p>
                </div>
              ) : tokenDetail?.site_token ? (
                <div className="flex flex-col gap-6">
                  {/* Basic Information */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-300">Basic Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {tokenDetail.site_token.id && (
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-1">ID</div>
                          <div className="text-gray-800">{tokenDetail.site_token.id}</div>
                        </div>
                      )}
                      {tokenDetail.site_token.token && (
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-1">Token</div>
                          <div className="text-gray-800 font-mono text-sm break-all">{tokenDetail.site_token.token}</div>
                        </div>
                      )}
                      {tokenDetail.site_token.name && (
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-1">Name</div>
                          <div className="text-gray-800">{tokenDetail.site_token.name}</div>
                        </div>
                      )}
                      {tokenDetail.site_token.uuid && (
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-1">UUID</div>
                          <div className="text-gray-800 font-mono text-sm break-all">{tokenDetail.site_token.uuid}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Configuration */}
                  {tokenDetail.site_token.config && Object.keys(tokenDetail.site_token.config).length > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-300">Configuration</h4>
                      <pre className="bg-white border border-gray-300 rounded p-3 font-mono text-xs max-h-[300px] overflow-y-auto whitespace-pre-wrap break-words">
                        {JSON.stringify(tokenDetail.site_token.config, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-5">No details available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteTokens;
